import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../i18n/I18nProvider";
import { toast } from "sonner";
import { Mail, Lock, ArrowLeft, ArrowRight, CheckCircle } from "lucide-react";

function formatErr(detail) {
  if (!detail) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" · ");
  return detail.msg || String(detail);
}

export default function ForgotPassword() {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const resetToken = searchParams.get("reset_token");

  // Mode: "request" (email-only form) or "reset" (token + new password)
  const mode = resetToken ? "reset" : "request";
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);

  const requestReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setDone(true);
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (pw.length < 8) {
      setErr(t("login.passwordHint"));
      return;
    }
    if (pw !== pw2) {
      setErr(t("forgot.mismatch"));
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.post("/auth/reset-password", { token: resetToken, new_password: pw });
      toast.success(t("forgot.resetOk"));
      navigate("/login", { replace: true });
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bone)] grid place-items-center px-6 py-10" data-testid="forgot-page">
      <div className="w-full max-w-md border border-[var(--ink-200)] bg-[var(--surface)] p-8">
        <Link to="/login" className="label-tech hover:text-[var(--ink)] inline-flex items-center gap-1">
          <ArrowLeft size={12} /> {t("login.title")}
        </Link>
        <h1 className="font-display text-3xl mt-3 tracking-tight">
          {mode === "reset" ? t("forgot.resetTitle") : t("forgot.requestTitle")}
        </h1>
        <p className="label-tech mt-2">
          {mode === "reset" ? t("forgot.resetSubtitle") : t("forgot.requestSubtitle")}
        </p>

        {mode === "request" && done ? (
          <div className="mt-6 border border-[var(--moss)] bg-[var(--moss)]/10 p-4" data-testid="forgot-sent-confirmation">
            <div className="flex gap-2 items-start">
              <CheckCircle size={16} className="text-[var(--moss)] mt-0.5 shrink-0" />
              <div>
                <div className="font-display text-sm">{t("forgot.sentTitle")}</div>
                <p className="label-tech mt-1">{t("forgot.sentBody")}</p>
              </div>
            </div>
          </div>
        ) : mode === "request" ? (
          <form onSubmit={requestReset} className="mt-6 space-y-4" data-testid="forgot-request-form">
            <div>
              <div className="label-tech mb-1 inline-flex items-center gap-1"><Mail size={12} /> {t("login.email")}</div>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-tech"
                placeholder="namn@foretag.se"
                data-testid="forgot-email-input"
                autoComplete="email"
              />
            </div>
            {err && <div className="border border-[var(--brick)] bg-[var(--brick)]/10 text-[var(--brick)] p-3 text-sm" data-testid="forgot-error">{err}</div>}
            <button type="submit" disabled={busy} className="btn-primary w-full justify-center py-3" data-testid="forgot-submit-btn">
              {busy ? "…" : t("forgot.requestSubmit")} <ArrowRight size={14} />
            </button>
          </form>
        ) : (
          <form onSubmit={submitReset} className="mt-6 space-y-4" data-testid="forgot-reset-form">
            <div>
              <div className="label-tech mb-1 inline-flex items-center gap-1"><Lock size={12} /> {t("forgot.newPassword")}</div>
              <input
                type="password"
                required
                minLength={8}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="input-tech"
                data-testid="reset-pw-input"
                autoComplete="new-password"
              />
            </div>
            <div>
              <div className="label-tech mb-1 inline-flex items-center gap-1"><Lock size={12} /> {t("forgot.confirmPassword")}</div>
              <input
                type="password"
                required
                minLength={8}
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                className="input-tech"
                data-testid="reset-pw2-input"
                autoComplete="new-password"
              />
            </div>
            {err && <div className="border border-[var(--brick)] bg-[var(--brick)]/10 text-[var(--brick)] p-3 text-sm" data-testid="reset-error">{err}</div>}
            <button type="submit" disabled={busy} className="btn-primary w-full justify-center py-3" data-testid="reset-submit-btn">
              {busy ? "…" : t("forgot.resetSubmit")} <ArrowRight size={14} />
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
